import { ErrorResponse } from '../../file';

/**
 * Gets a user-friendly error message from an ErrorResponse
 */
export const getErrorMessage = (error?: ErrorResponse): string => {
   if (!error) {
      return 'An unexpected error occurred';
   }

   return error.message || error.title || 'An unexpected error occurred';
};

/**
 * Checks if the error is a specific HTTP status code
 */
export const isErrorStatus = (error?: ErrorResponse, status?: number): boolean => {
   return error?.status === status;
};

/**
 * Checks if the error is a validation error (400)
 */
export const isValidationError = (error?: ErrorResponse): boolean => {
   return isErrorStatus(error, 400);
};

/**
 * Checks if the error is an unauthorized error (401)
 */
export const isUnauthorizedError = (error?: ErrorResponse): boolean => {
   return isErrorStatus(error, 401);
};

/**
 * Checks if the error is a forbidden error (403)
 */
export const isForbiddenError = (error?: ErrorResponse): boolean => {
   return isErrorStatus(error, 403);
};

/**
 * Checks if the error is a not found error (404)
 */
export const isNotFoundError = (error?: ErrorResponse): boolean => {
   return isErrorStatus(error, 404);
};

/**
 * Checks if the error is a server error (5xx)
 */
export const isServerError = (error?: ErrorResponse): boolean => {
   return (error?.status ?? 0) >= 500 && (error?.status ?? 0) < 600;
};

/**
 * Checks if the error is a network error
 */
export const isNetworkError = (error?: ErrorResponse): boolean => {
   return error?.type === 'NetworkError' || error?.status === 0;
};

/**
 * Logs error details in a structured format
 */
export const logError = (context: string, error?: ErrorResponse): void => {
   if (!error) return;

   console.error(`[${context}] API Error:`, {
      type: error.type,
      title: error.title,
      message: error.message,
      status: error.status,
      timestamp: error.timestamp,
      traceId: error.traceId,
      details: error.details
   });
};

/**
 * Gets validation error details if available
 */
export const getValidationErrors = (error?: ErrorResponse): Record<string, any> | undefined => {
   if (!isValidationError(error)) {
      return undefined;
   }

   return error?.details;
};

